/**
 * TasksOnboarding — Tasks & Records 소개 페이지
 *
 * Firebase 제품 소개 페이지 스타일.
 * 아직 활성화되지 않은 채널에서 태스크 탭 클릭 시 표시.
 */
import React from 'react';
import { Trans } from '@lingui/macro';
import {
  ListTodo, MessageSquareText, Search, Loader2, Sparkles, Plus,
} from 'lucide-react';
import { Button } from '../../components/ui/button';

interface TasksOnboardingProps {
  onActivate: () => void;
  activating: boolean;
}

const TasksOnboarding: React.FC<TasksOnboardingProps> = ({
  onActivate,
  activating,
}) => {
  const features = [
    {
      icon: MessageSquareText,
      title: <Trans>Capture conversations as tasks</Trans>,
      desc: <Trans>Create task cards directly from threads. Never let instructions get buried in chat.</Trans>,
    },
    {
      icon: Plus,
      title: <Trans>Create tasks directly</Trans>,
      desc: <Trans>Create tasks without a conversation and set assignees and due dates.</Trans>,
    },
    {
      icon: Sparkles,
      title: <Trans>AI auto-organize</Trans>,
      desc: <Trans>AI analyzes conversations and suggests titles, summaries, assignees, and due dates.</Trans>,
    },
    {
      icon: Search,
      title: <Trans>Searchable Records</Trans>,
      desc: <Trans>Completed tasks are saved to Records with AI tags so you can find them anytime.</Trans>,
    },
  ];

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 py-12 overflow-y-auto">
      <div className="max-w-md w-full flex flex-col items-center gap-8">
        {/* 아이콘 + 타이틀 */}
        <div className="flex flex-col items-center gap-3">
          <div className="size-16 rounded-2xl bg-gradient-to-br from-blue-400/20 to-indigo-500/20 flex items-center justify-center">
            <ListTodo className="size-8 text-primary" />
          </div>
          <h2 className="text-lg font-bold text-[color:rgb(var(--text-title))]">
            <Trans>Tasks & Records</Trans>
          </h2>
          <p className="text-sm text-muted-foreground text-center leading-relaxed max-w-[320px]">
            <Trans>
              Capture tasks from chat conversations and manage them as cards.
              Never lose track of what was discussed.
            </Trans>
          </p>
        </div>

        {/* 기능 카드 */}
        <div className="w-full flex flex-col gap-3">
          {features.map((feature, i) => (
            <div
              key={i}
              className="flex items-start gap-3.5 p-3.5 rounded-xl bg-muted/30 border border-border/50"
            >
              <div className="size-9 rounded-lg bg-primary/8 flex items-center justify-center shrink-0 mt-0.5">
                <feature.icon className="size-4 text-primary" />
              </div>
              <div className="min-w-0">
                <h4 className="text-label font-semibold text-[color:rgb(var(--text-title))] mb-0.5">
                  {feature.title}
                </h4>
                <p className="text-meta text-muted-foreground leading-relaxed">
                  {feature.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* 시작하기 버튼 */}
        <Button
          onClick={onActivate}
          disabled={activating}
          className="gap-2 px-6"
        >
          {activating ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              <Trans>Setting up...</Trans>
            </>
          ) : (
            <>
              <ListTodo className="size-4" />
              <Trans>Get Started</Trans>
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default TasksOnboarding;
